import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CookingPot, Plus, Trash, MagnifyingGlass, Sparkle, PencilSimple, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PT_LABELS = {
  gravida: 'Super Barriguinhas',
  introducao_alimentar: 'Super Bebés',
  pos_parto: 'Super Mamãs',
  pre_concecao: 'Super Pré Mamãs',
  geral: 'Geral',
};

const CAT_LABELS = {
  'pequeno-almoco': 'Peq.-almoço',
  'almoco': 'Almoço',
  'jantar': 'Jantar',
  'lanche': 'Lanche',
  'ceia': 'Ceia',
};

function fmt(n) { return n == null ? '-' : (Math.round(n * 10) / 10).toString(); }

function MacroDelta({ slot, computed }) {
  if (!slot && !computed) return null;
  const Row = ({ label, target, actual, unit }) => {
    const diff = (actual ?? 0) - (target ?? 0);
    const pct = target ? (actual ?? 0) / target : 0;
    const colour = pct > 1.15 ? 'text-amber-600' : pct < 0.85 ? 'text-amber-600' : 'text-brand-green';
    return (
      <div className="grid grid-cols-4 gap-2 text-[11px] py-1 border-b border-brand-border/40 last:border-0">
        <span className="text-brand-muted">{label}</span>
        <span className="text-brand-text">{fmt(target)}{unit}</span>
        <span className="text-brand-text font-medium">{fmt(actual)}{unit}</span>
        <span className={`${colour} font-medium`}>{diff > 0 ? '+' : ''}{fmt(diff)}{unit}</span>
      </div>
    );
  };
  return (
    <div className="bg-brand-bg/30 rounded-xl p-3 border border-brand-border/60 mt-2">
      <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wider text-brand-placeholder pb-1 border-b border-brand-border/60">
        <span>Macro</span><span>Plano</span><span>Receita</span><span>Δ</span>
      </div>
      <Row label="Energia" target={slot?.energia_kcal} actual={computed?.energia_kcal} unit=" kcal" />
      <Row label="Proteína" target={slot?.proteina_g} actual={computed?.proteina_g} unit=" g" />
      <Row label="HC" target={slot?.hidratos_g} actual={computed?.hidratos_g} unit=" g" />
      <Row label="Gordura" target={slot?.gordura_g} actual={computed?.gordura_g} unit=" g" />
    </div>
  );
}

function AllocationPanel({ patientId, recipe, mealSlots, initial, onSaved, onCancel }) {
  const [slot, setSlot] = useState(initial?.meal_slot || (mealSlots[0]?.key || ''));
  const [portions, setPortions] = useState(initial?.portions_for_patient ?? 1);
  const [note, setNote] = useState(initial?.nutritionist_note || '');
  const [slotData, setSlotData] = useState({ has_plan: false, slot_macros: null, per_portion: null, suggested_portions: 1, plan_name: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSlot = useCallback(async () => {
    if (!recipe?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (slot) params.set('meal_slot', slot);
      params.set('recipe_id', recipe.id);
      const { data } = await axios.get(`${API}/api/patients/${patientId}/recipes/slot-macros?${params.toString()}`);
      setSlotData(data || {});
      // Auto-suggest portions only if user has not set portions yet (initial open)
      if (initial?.portions_for_patient == null && data?.suggested_portions) {
        setPortions(data.suggested_portions);
      }
    } catch {
      // silent
    } finally { setLoading(false); }
  }, [patientId, recipe?.id, slot, initial?.portions_for_patient]);

  useEffect(() => { fetchSlot(); }, [fetchSlot]);

  const computed = useMemo(() => {
    const pp = slotData?.per_portion;
    if (!pp) return null;
    const p = parseFloat(portions) || 0;
    return {
      energia_kcal: pp.energia_kcal * p,
      proteina_g: pp.proteina_g * p,
      hidratos_g: pp.hidratos_g * p,
      gordura_g: pp.gordura_g * p,
      fibra_g: pp.fibra_g * p,
    };
  }, [slotData, portions]);

  const adjustPortions = (delta) => {
    const next = Math.max(0.5, (parseFloat(portions) || 0) + delta);
    setPortions(Math.round(next * 2) / 2);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/patients/${patientId}/recipes/${recipe.id}`, {
        type: 'manual',
        meal_slot: slot,
        portions_for_patient: parseFloat(portions) || 0,
        nutritionist_note: note,
      });
      toast.success('Alocação guardada');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro a guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="allocation-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-brand-placeholder mb-1">Receita</p>
        <p className="text-sm font-medium text-brand-text">{recipe.name}</p>
        {slotData.per_portion && (slotData.per_portion.energia_kcal > 0 || slotData.per_portion.proteina_g > 0) ? (
          <p className="text-[11px] text-brand-muted mt-0.5">
            Por dose: {fmt(slotData.per_portion.energia_kcal)} kcal · P {fmt(slotData.per_portion.proteina_g)}g · HC {fmt(slotData.per_portion.hidratos_g)}g · G {fmt(slotData.per_portion.gordura_g)}g
          </p>
        ) : slotData.per_portion ? (
          <p className="text-[11px] text-amber-600 mt-0.5" data-testid="alloc-no-nutrition">
            Receita sem alimentos calculáveis. Adiciona ingredientes ligados à base de dados para cálculo automático.
          </p>
        ) : null}
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-brand-placeholder block mb-1">Refeição</label>
        <div className="flex flex-wrap gap-1.5">
          {mealSlots.map(ms => (
            <button
              key={ms.key}
              onClick={() => setSlot(ms.key)}
              className={`px-3 py-1.5 rounded-full text-xs transition-all ${slot === ms.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
              data-testid={`alloc-slot-${ms.key}`}
            >{ms.label}</button>
          ))}
        </div>
      </div>

      {!loading && !slotData.has_plan && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-[11px] text-amber-800" data-testid="alloc-warning-no-plan">
          <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
          <span>Esta cliente ainda não tem plano alimentar activo. Sem macros para esta refeição.</span>
        </div>
      )}
      {!loading && slotData.has_plan && !slotData.slot_macros && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-[11px] text-amber-800" data-testid="alloc-warning-no-slot">
          <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
          <span>O plano <b>{slotData.plan_name || 'activo'}</b> não tem alimentos definidos para esta refeição.</span>
        </div>
      )}

      <div>
        <label className="text-[11px] uppercase tracking-wider text-brand-placeholder block mb-1">Doses sugeridas a preparar</label>
        <div className="flex items-center gap-2">
          <button onClick={() => adjustPortions(-0.5)} className="w-8 h-8 rounded-full border border-brand-border text-brand-muted hover:bg-brand-bg" data-testid="alloc-portion-minus">−</button>
          <Input
            type="number" step="0.5" min="0.5"
            value={portions}
            onChange={e => setPortions(e.target.value)}
            className="rounded-xl border-brand-border text-sm text-center w-24"
            data-testid="alloc-portions"
          />
          <button onClick={() => adjustPortions(0.5)} className="w-8 h-8 rounded-full border border-brand-border text-brand-muted hover:bg-brand-bg" data-testid="alloc-portion-plus">+</button>
          {slotData.suggested_portions ? (
            <button onClick={() => setPortions(slotData.suggested_portions)} className="text-[11px] text-brand-green hover:underline ml-1" data-testid="alloc-use-suggested">
              Usar sugestão ({slotData.suggested_portions})
            </button>
          ) : null}
        </div>
      </div>

      <MacroDelta slot={slotData.slot_macros} computed={computed} />

      <div>
        <label className="text-[11px] uppercase tracking-wider text-brand-placeholder block mb-1">Nota da nutricionista (opcional)</label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          placeholder="Ex: ajusta o azeite para 1 c/sopa…"
          className="w-full rounded-xl border border-brand-border text-xs p-2 focus:outline-none focus:border-brand-green resize-none"
          data-testid="alloc-note"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onCancel} className="text-xs text-brand-muted hover:text-brand-text px-3 py-2" data-testid="alloc-cancel">Cancelar</button>
        <button onClick={save} disabled={saving} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid="alloc-save">
          {saving ? 'A guardar…' : 'Guardar alocação'}
        </button>
      </div>
    </div>
  );
}

function AllocationModal({ open, onClose, recipe, patientId, mealSlots, initial, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Alocar receita ao plano</DialogTitle></DialogHeader>
        {recipe && (
          <AllocationPanel
            patientId={patientId}
            recipe={recipe}
            mealSlots={mealSlots}
            initial={initial}
            onSaved={() => { onSaved(); onClose(); }}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecipeCard({ recipe, allocation, mealSlotsLabel, onRemove, onEdit, onClick, badge, removeLabel }) {
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-3 hover:border-brand-green/40 transition-colors group" data-testid={`recipe-card-${recipe.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
          <CookingPot size={18} className="text-brand-green" weight="duotone" />
        </div>
        <button onClick={onClick} className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium text-brand-text truncate">{recipe.name}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {recipe.category && (
              <span className="text-[10px] bg-brand-bg text-brand-muted px-1.5 py-0.5 rounded-full">{CAT_LABELS[recipe.category] || recipe.category}</span>
            )}
            {(recipe.tags || []).slice(0, 3).map(t => (
              <span key={t} className="text-[10px] bg-brand-mint/10 text-brand-green px-1.5 py-0.5 rounded-full">{t}</span>
            ))}
            {badge && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{badge}</span>}
          </div>
        </button>
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button onClick={onEdit} className="text-brand-muted hover:text-brand-green p-1" title="Editar alocação" data-testid={`edit-recipe-${recipe.id}`}>
              <PencilSimple size={14} />
            </button>
          )}
          {onRemove && (
            <button onClick={onRemove} className="text-brand-muted hover:text-red-500 p-1" title={removeLabel || 'Remover'} data-testid={`remove-recipe-${recipe.id}`}>
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>
      {allocation && (allocation.meal_slot || allocation.portions_for_patient || allocation.nutritionist_note) && (
        <div className="mt-2 pt-2 border-t border-brand-border/50 space-y-1" data-testid={`alloc-summary-${recipe.id}`}>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            {allocation.meal_slot && (
              <span className="bg-brand-green/10 text-brand-green px-2 py-0.5 rounded-full font-medium">
                {mealSlotsLabel[allocation.meal_slot] || allocation.meal_slot}
              </span>
            )}
            {allocation.portions_for_patient ? (
              <span className="bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{allocation.portions_for_patient} {allocation.portions_for_patient === 1 ? 'dose' : 'doses'}</span>
            ) : null}
            {allocation.computed_macros && (
              <span className="text-brand-muted">
                {fmt(allocation.computed_macros.energia_kcal)} kcal · P {fmt(allocation.computed_macros.proteina_g)}g
              </span>
            )}
          </div>
          {allocation.nutritionist_note && (
            <p className="text-[11px] text-brand-muted italic line-clamp-2">“{allocation.nutritionist_note}”</p>
          )}
        </div>
      )}
    </div>
  );
}

function RecipeViewModal({ recipe, onClose }) {
  if (!recipe) return null;
  return (
    <Dialog open={!!recipe} onOpenChange={() => onClose()}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">{recipe.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {(recipe.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(recipe.tags || []).map(t => (
                <span key={t} className="text-[10px] bg-brand-mint/10 text-brand-green px-2 py-0.5 rounded-full">{t}</span>
              ))}
            </div>
          )}
          {(recipe.ingredients || []).length > 0 && (
            <div>
              <p className="text-[11px] uppercase text-brand-muted mb-1">Ingredientes</p>
              <ul className="space-y-1">
                {(recipe.ingredients || []).map((ing, i) => (
                  <li key={i} className="text-xs text-brand-text">
                    {typeof ing === 'string' ? ing : `${ing.quantity || ''} ${ing.unit || ''} ${ing.custom_name || ing.name || ''}`.trim()}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {recipe.instructions && (
            <div>
              <p className="text-[11px] uppercase text-brand-muted mb-1">Modo de preparação</p>
              <p className="text-xs text-brand-text whitespace-pre-line">{recipe.instructions}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddRecipeModal({ open, onClose, onPick, alreadyLinkedIds }) {
  const [search, setSearch] = useState('');
  const [allRecipes, setAllRecipes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/api/recipes`).then(({ data }) => setAllRecipes(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return (allRecipes || []).filter(r => !alreadyLinkedIds.has(r.id) && (!s || r.name.toLowerCase().includes(s) || (r.tags || []).some(t => t.toLowerCase().includes(s))));
  }, [allRecipes, search, alreadyLinkedIds]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Adicionar receita ao cliente</DialogTitle></DialogHeader>
        <div className="relative mb-3">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Pesquisar por nome ou tag…"
            className="pl-8 rounded-xl border-brand-border text-sm"
            data-testid="add-recipe-search"
          />
        </div>
        <div className="overflow-y-auto flex-1 space-y-2">
          {loading && <p className="text-center text-xs text-brand-muted py-6">A carregar…</p>}
          {!loading && filtered.length === 0 && <p className="text-center text-xs text-brand-muted py-6">Nenhuma receita encontrada</p>}
          {filtered.map(r => (
            <button
              key={r.id}
              onClick={() => onPick(r)}
              className="w-full text-left bg-brand-bg/30 hover:bg-brand-mint/10 border border-brand-border rounded-2xl p-3 flex items-start gap-3 transition-colors"
              data-testid={`pick-recipe-${r.id}`}
            >
              <CookingPot size={16} className="text-brand-green mt-0.5 flex-shrink-0" weight="duotone" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-text truncate">{r.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {r.category && <span className="text-[10px] bg-white text-brand-muted px-1.5 py-0.5 rounded-full">{CAT_LABELS[r.category] || r.category}</span>}
                  {(r.tags || []).slice(0, 4).map(t => <span key={t} className="text-[10px] bg-brand-mint/10 text-brand-green px-1.5 py-0.5 rounded-full">{t}</span>)}
                </div>
              </div>
              <Plus size={14} className="text-brand-green flex-shrink-0 mt-1" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RecipesTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [data, setData] = useState({ suggested: [], manual: [], excluded_ids: [], program_phase: '', patient_type: '', meal_slots: [], has_active_plan: false });
  const [loading, setLoading] = useState(true);
  const [showPick, setShowPick] = useState(false);
  const [allocating, setAllocating] = useState(null);   // {recipe, initial}
  const [viewing, setViewing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/patients/${patientId}/recipes`);
      setData({
        suggested: Array.isArray(data?.suggested) ? data.suggested : [],
        manual: Array.isArray(data?.manual) ? data.manual : [],
        excluded_ids: Array.isArray(data?.excluded_ids) ? data.excluded_ids : [],
        program_phase: data?.program_phase || '',
        patient_type: data?.patient_type || '',
        meal_slots: Array.isArray(data?.meal_slots) ? data.meal_slots : [],
        has_active_plan: !!data?.has_active_plan,
      });
    } catch {
      toast.error('Erro a carregar receitas');
    } finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const linkedIds = useMemo(() => new Set([
    ...data.suggested.map(r => r.id),
    ...data.manual.map(r => r.id),
  ]), [data]);

  const slotsLabelMap = useMemo(() => {
    const m = {};
    (data.meal_slots || []).forEach(s => { m[s.key] = s.label; });
    return m;
  }, [data.meal_slots]);

  const onPickRecipe = (recipe) => {
    setShowPick(false);
    setAllocating({ recipe, initial: null });
  };

  const removeManual = async (recipe) => {
    const ok = await confirmDelete({ description: `Vai remover a receita "${recipe.name || recipe.title || ''}" da ficha da paciente.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/patients/${patientId}/recipes/${recipe.id}`);
      toast.success('Removida');
      fetchData();
    } catch { toast.error('Erro'); }
  };
  const excludeFromSuggested = async (recipe) => {
    try {
      await axios.post(`${API}/api/patients/${patientId}/recipes/${recipe.id}`, { type: 'excluded' });
      toast.success('Sugestão removida');
      fetchData();
    } catch { toast.error('Erro'); }
  };

  // Group suggestions by category
  const grouped = useMemo(() => {
    const out = {};
    (data.suggested || []).forEach(r => {
      const cat = r.category || 'outros';
      if (!out[cat]) out[cat] = [];
      out[cat].push(r);
    });
    return out;
  }, [data]);

  const ptLabel = PT_LABELS[data.patient_type] || 'Sem programa';
  const hasProgram = !!data.patient_type && data.patient_type !== 'geral';

  if (loading) return <div className="text-center py-8 text-sm text-brand-muted">A carregar receitas…</div>;

  return (
    <div className="space-y-5" data-testid="admin-recipes-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-brand-muted">Receitas para esta cliente</p>
          <p className="text-sm font-medium text-brand-text">
            {ptLabel}{data.program_phase ? ` · fase ${data.program_phase}` : ''}
            {!data.has_active_plan && <span className="ml-2 text-[10px] text-amber-600">(sem plano activo)</span>}
          </p>
        </div>
        <button
          onClick={() => setShowPick(true)}
          className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2"
          data-testid="add-recipe-btn"
        >
          <Plus size={14} /> Adicionar receita
        </button>
      </div>

      {!hasProgram && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs text-amber-800">
          Define um programa no separador <strong>Resumo</strong> para activar sugestões automáticas.
        </div>
      )}

      {/* Suggested */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={14} className="text-brand-green" weight="fill" />
          <h3 className="font-heading text-sm text-brand-text">Sugestões automáticas</h3>
          <span className="text-[10px] text-brand-muted">({data.suggested.length})</span>
        </div>
        {data.suggested.length === 0 ? (
          <p className="text-xs text-brand-placeholder italic">Sem receitas a sugerir para este programa/fase.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(grouped).map(([cat, recs]) => (
              <div key={cat}>
                <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-1.5">{CAT_LABELS[cat] || cat}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {recs.map(r => (
                    <RecipeCard
                      key={r.id}
                      recipe={r}
                      mealSlotsLabel={slotsLabelMap}
                      onClick={() => setViewing(r)}
                      onEdit={() => setAllocating({ recipe: r, initial: null })}
                      onRemove={() => excludeFromSuggested(r)}
                      removeLabel="Remover desta cliente"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Manual / allocated */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-heading text-sm text-brand-text">Alocadas ao plano</h3>
          <span className="text-[10px] text-brand-muted">({data.manual.length})</span>
        </div>
        {data.manual.length === 0 ? (
          <p className="text-xs text-brand-placeholder italic">Ainda não alocaste receitas ao plano desta cliente.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {data.manual.map(r => (
              <RecipeCard
                key={r.id}
                recipe={r}
                allocation={r.allocation}
                mealSlotsLabel={slotsLabelMap}
                onClick={() => setViewing(r)}
                onEdit={() => setAllocating({ recipe: r, initial: r.allocation })}
                onRemove={() => removeManual(r)}
                removeLabel="Remover"
                badge={r.allocation?.meal_slot ? null : 'Sem refeição'}
              />
            ))}
          </div>
        )}
      </section>

      <AddRecipeModal open={showPick} onClose={() => setShowPick(false)} onPick={onPickRecipe} alreadyLinkedIds={linkedIds} />
      <AllocationModal
        open={!!allocating}
        onClose={() => setAllocating(null)}
        recipe={allocating?.recipe}
        patientId={patientId}
        mealSlots={data.meal_slots}
        initial={allocating?.initial}
        onSaved={fetchData}
      />
      <RecipeViewModal recipe={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
